import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import TableSearch from "../../Components/Table/components/TableSearch";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { getUsers } from "../../Redux/Slicers/Users/UsersSlicer";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { ReactComponent as Edit } from "../../Assets/Images/Edit.svg";
import { ReactComponent as Delete } from "../../Assets/Images/Delete.svg";
import Dialog from "../../Components/Dialog/Dialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { ReactComponent as Cancel } from "../../Assets/Images/Cancel.svg";
import CreateUserModal from "./components/CreateUserModal";
import EditUserModal from "./components/EditUserModal";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import { LockOpen } from "@mui/icons-material";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import FailureResponseDialog from "../../Components/FailureResponseDialog/FailureResponseDialog";
import {
  clearUnlockMaster,
  unlockMaster,
} from "../../Redux/Slicers/Master/UnlockMasterSlice";
import {
  clearUpdateUserState,
} from "../../Redux/Slicers/Users/UsersUpdateSlicer";
import {
  clearDeleteUser,
  deleteUser,
} from "../../Redux/Slicers/Users/UsersDeleteSlice";

export interface IUserRow {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  otp_locked?: boolean;
  index: number;
}

const Users: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { data, loading } = useAppSelector((state) => state.Users);
  const { data: unlockMasterData } = useAppSelector(
    (state) => state.UnlockMaster
  );
  const { data: updateUserData } = useAppSelector((state) => state.UsersUpdate);
  const { data: deleteUserData } = useAppSelector((state) => state.UsersDelete);

  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<IUserRow | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteableName, setDeleteableName] = useState<string>("");
  const [deleteableId, setDeleteableId] = useState<string>("");

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  // Refresh user list after unlock success
  useEffect(() => {
    if (
      unlockMasterData?.response?.status === true ||
      unlockMasterData?.response?.responseCode === 200
    ) {
      dispatch(getUsers());
      dispatch(clearUnlockMaster());
    }
  }, [unlockMasterData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearUnlockMaster());
    };
  }, [dispatch]);

  useEffect(() => {
    const d = deleteUserData as { responseCode?: number };
    if (d?.responseCode === 200) {
      dispatch(getUsers());
      dispatch(clearDeleteUser());
    }
  }, [deleteUserData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearUpdateUserState());
      dispatch(clearDeleteUser());
    };
  }, [dispatch]);

  const rows: IUserRow[] = useMemo(() => {
    const list = data?.response?.data ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((user: any, index: number) => ({
      id: user._id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      otp_locked: user.otp_locked,
      index: index + 1,
    }));
  }, [data]);

  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      flex: 0.5,
      headerAlign: "center",
      align: "center",
      type: "number",
      maxWidth: 100,
    },
    {
      field: "first_name",
      renderHeader: () => <b>First Name</b>,
      flex: 1,
      type: "string",
    },
    {
      field: "last_name",
      renderHeader: () => <b>Last Name</b>,
      flex: 1,
      type: "string",
    },
    {
      field: "email",
      renderHeader: () => <b>Email</b>,
      flex: 1.5,
      type: "string",
    },
    {
      field: "phone",
      renderHeader: () => <b>Phone</b>,
      flex: 1,
      type: "string",
    },
    {
      field: "otp_locked",
      renderHeader: () => <b>OTP Status</b>,
      flex: 1,
      type: "boolean",
      maxWidth: 180,
      renderCell: (params) => <div>{params.value ? "Locked" : "Active"}</div>,
      cellClassName: (params) => (params.value ? "otp-locked" : "otp-active"),
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      getActions: (params) => {
        const actions: JSX.Element[] = [];

        // Add Unlock button only if OTP is locked
        if (params.row.otp_locked && params.row.phone) {
          actions.push(
            <GridActionsCellItem
              key="Unlock"
              label="Unlock OTP"
              icon={<LockOpen className="action_icons" />}
              onClick={() => {
                dispatch(unlockMaster({ phone: params.row.phone! }));
              }}
            />
          );
        }

        actions.push(
          <GridActionsCellItem
            key="Edit"
            label="Edit"
            icon={<Edit className="action_icons" />}
            onClick={() => {
              setEditingUser(params.row);
              setOpenEdit(true);
            }}
          />
        );

        actions.push(
          <GridActionsCellItem
            key="Delete"
            label="Delete"
            icon={<Delete className="action_icons" />}
            onClick={() => {
              const label =
                [params.row.first_name, params.row.last_name]
                  .filter(Boolean)
                  .join(" ") ||
                params.row.email ||
                "this user";
              setDeleteableName(label);
              setDeleteableId(params.row.id);
              setDeleteOpen(true);
            }}
          />
        );

        return actions;
      },
      cellClassName: "action_cell",
    },
  ];

  return (
    <>
      <Table rows={rows} columns={columns} loading={loading}>
        <CustomToolbar>
          <h6>Users</h6>
          <TableSearch />
          <ButtonField
            className="add_button"
            onClick={() => setOpenCreate(true)}
          >
            Create User
          </ButtonField>
        </CustomToolbar>
      </Table>

      <Dialog
        open={openCreate}
        dialogClassName="master_dialog"
        dialogTitleClassName="master_dialog_title"
        Dialog_Title={
          <>
            Create User
            <IconButtonField onClick={() => setOpenCreate(false)}>
              <Cancel />
            </IconButtonField>
          </>
        }
        Dialog_Content={<CreateUserModal onClose={() => setOpenCreate(false)} />}
        dialogActionClassName="master_dialog_action"
      />

      <Dialog
        open={openEdit}
        dialogClassName="master_dialog"
        dialogTitleClassName="master_dialog_title"
        Dialog_Title={
          <>
            Edit User
            <IconButtonField
              onClick={() => {
                setOpenEdit(false);
                setEditingUser(null);
                dispatch(clearUpdateUserState());
              }}
            >
              <Cancel />
            </IconButtonField>
          </>
        }
        Dialog_Content={
          editingUser ? (
            <EditUserModal
              user={editingUser}
              onClose={() => {
                setOpenEdit(false);
                setEditingUser(null);
                dispatch(clearUpdateUserState());
              }}
            />
          ) : undefined
        }
        dialogActionClassName="master_dialog_action"
      />

      <DeleteDialog
        deleteableName={deleteableName}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        deleteFunction={() => dispatch(deleteUser(deleteableId))}
        response={deleteUserData}
      />

      <ResponseDialog
        response={unlockMasterData}
        parentDialog={() => dispatch(clearUnlockMaster())}
      />
      <FailureResponseDialog response={unlockMasterData} />

      <ResponseDialog
        response={updateUserData as any}
        onClose={() => {
          const d = updateUserData as { responseCode?: number };
          if (d?.responseCode === 200) {
            dispatch(getUsers());
            setOpenEdit(false);
            setEditingUser(null);
          }
          dispatch(clearUpdateUserState());
        }}
      />
    </>
  );
};

export default Users;

